import React, { useContext } from "react";
import { isNil } from "lodash";
import { NavigationControl } from "react-map-gl";
import { useHistory } from "react-router-dom";
import { buildLink } from "common/routing";
import { formatAddressFromAddressParts } from "common/utils/address";
import {
  CertificateUploadStatus,
  GetDocumentUploadQuery,
} from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { AuthContext } from "../Authorization/AuthContext";
import { EmptyState } from "../Common/EmptyState";
import { LayeredMap } from "../Maps/LayeredMap";
import { LayerContextProvider } from "../Maps/layers";
import useViewport from "../Maps/utils/viewportHook";
import {
  Container,
  ExtractedAddressContainer,
  ExtractedAddressSection as ExtractedAddressSectionContainer,
  Header,
  LocationSection,
  ParcelLocation,
  ParcelSection,
  PropertyLocation,
  PropertyLocationSection,
} from "./__styles__/Location";

type ExtractedAddress = {
  streetAddress?: Maybe<string>;
  city?: Maybe<string>;
  state?: Maybe<string>;
  zipcode?: Maybe<string>;
};

const PropertyAssignedState = ({
  property,
}: {
  property: NonNullable<
    NonNullable<GetDocumentUploadQuery["documentUpload"]>["property"]
  >;
}) => {
  const { viewport, setViewport } = useViewport({
    zoom: 17,
    latitude: property.latitude,
    longitude: property.longitude,
  });
  const { account } = useContext(AuthContext);
  if (!account) {
    return null;
  }
  const layers = {
    marker: {
      value: { latitude: property.latitude!, longitude: property.longitude! },
    },
    parcels: {}, // nothing provided as to not highlight the 'selected' parcel on the map
  };

  return (
    <React.Fragment>
      <LocationSection>
        <div>
          <PropertyLocation>{property.streetAddress}</PropertyLocation>
          <PropertyLocation>
            {formatAddressFromAddressParts({
              city: property.city,
              state: property.state,
              zipcode: property.zipcode,
            })}
          </PropertyLocation>
        </div>
        <ParcelSection>
          <ParcelLocation>
            Parcel ID: {property.parcel?.parcelId}
          </ParcelLocation>
          <ParcelLocation>
            Block number: {property.parcel?.block}
          </ParcelLocation>
          <ParcelLocation>Lot number: {property.parcel?.lot}</ParcelLocation>
        </ParcelSection>
      </LocationSection>
      <Container>
        <LayerContextProvider account={account}>
          <LayeredMap
            width={"100%"}
            height={"297px"}
            setViewport={setViewport}
            viewport={viewport}
            baseMapStyle={account?.defaultBaseMap.mapboxStyle}
            account={account}
            layers={layers}
            halfMap={true}
            bottomRightMapOverlayItems={[
              <NavigationControl showCompass={false} />,
            ]}
          />
        </LayerContextProvider>
      </Container>
    </React.Fragment>
  );
};

const DocumentNeedsGeocodingState = ({
  documentUpload,
}: {
  documentUpload: NonNullable<GetDocumentUploadQuery["documentUpload"]>;
}) => {
  const history = useHistory();
  return (
    <React.Fragment>
      <EmptyState
        title="This file is missing a location"
        body="Add a location to view it on your map and property list"
        icon="missing-location"
        primaryButtonProps={{
          label: "Add location",
          onClick: () =>
            history.push(
              buildLink("geocodeDocument", { id: documentUpload.id }),
            ),
        }}
      />
    </React.Fragment>
  );
};

const ExtractionInProgressState = () => {
  return (
    <EmptyState
      icon="missing-incomplete"
      title="Elevation Certificate is processing"
      body="We're extracting details, checking for errors, and adding the location. Check back soon to see the results."
      paddingTop={spacing["3xl"].value}
    />
  );
};

const ExtractedAddressSection = ({
  extractedAddress,
}: {
  extractedAddress?: Maybe<ExtractedAddress>;
}) => {
  let extractedAddressComponent = (
    <React.Fragment>
      <PropertyLocation>{extractedAddress?.streetAddress}</PropertyLocation>
      <PropertyLocation>
        {formatAddressFromAddressParts({
          city: extractedAddress?.city,
          state: extractedAddress?.state,
          zipcode: extractedAddress?.zipcode,
        })}
      </PropertyLocation>
    </React.Fragment>
  );
  if (
    !extractedAddress ||
    (isNil(extractedAddress.streetAddress) &&
      isNil(extractedAddress.city) &&
      isNil(extractedAddress.state) &&
      isNil(extractedAddress.zipcode))
  ) {
    extractedAddressComponent = (
      <EmptyState compact title={"No address detected"} paddingTop={0} />
    );
  }

  return (
    <ExtractedAddressSectionContainer>
      <Header>Extracted address</Header>
      <ExtractedAddressContainer>
        {extractedAddressComponent}
      </ExtractedAddressContainer>
    </ExtractedAddressSectionContainer>
  );
};

const Location = ({
  documentUpload,
  extractedAddress,
}: {
  documentUpload: NonNullable<GetDocumentUploadQuery["documentUpload"]>;
  extractedAddress?: Maybe<ExtractedAddress>;
}) => {
  if (
    documentUpload.certificateUpload?.status ===
    CertificateUploadStatus.PROCESSING
  ) {
    return <ExtractionInProgressState />;
  }

  return (
    <React.Fragment>
      <ExtractedAddressSection extractedAddress={extractedAddress} />
      <PropertyLocationSection>
        <Header>Property location</Header>
        {documentUpload.property ?
          <PropertyAssignedState property={documentUpload.property} />
        : <DocumentNeedsGeocodingState documentUpload={documentUpload} />}
      </PropertyLocationSection>
    </React.Fragment>
  );
};

export default Location;
